import React from 'react';
import { FileText, RefreshCw, Shield, ArrowRight, ExternalLink, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';

const AffiliateSection = () => {
  const steps = [
    {
      step: 1,
      name: "Create",
      app: "Legal Document Generator",
      description: "Generate contracts, NDAs, and business agreements with AI assistance",
      url: "https://legaldocgenerator.ca",
      icon: FileText,
      color: "bg-emerald-500"
    },
    {
      step: 2,
      name: "Convert",
      app: "LegalDocConverter",
      description: "Convert formats, scan documents, get plain-English explanations",
      url: null, // Current app
      icon: RefreshCw,
      color: "bg-blue-500",
      current: true
    },
    {
      step: 3,
      name: "Review",
      app: "ContractReviewPro",
      description: "AI analyzes contracts for risks and unfair terms before you sign",
      url: "https://contractreviewpro.abacusai.app",
      icon: Shield,
      color: "bg-purple-500"
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-r from-slate-800 to-slate-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            The Simple 3-Step Workflow
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto">
            Handle your entire legal document workflow without expensive lawyer fees
          </p>
        </div>

        {/* 3 Steps */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div key={index} className="flex items-center">
                {/* Step Card */}
                <div className={`relative bg-white/10 backdrop-blur-sm rounded-xl p-5 border ${
                  step.current ? 'border-blue-400 ring-2 ring-blue-400/50' : 'border-white/10'
                } hover:bg-white/15 transition-all w-full md:w-auto`}>
                  {/* Step Number */}
                  <div className={`absolute -top-3 -left-3 w-8 h-8 ${step.color} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                    {step.step}
                  </div>
                  
                  {step.current && (
                    <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-blue-500 text-white text-xs font-medium rounded-full">
                      You're here
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-2 pt-1">
                    <div className={`p-2 ${step.color} rounded-lg`}>
                      <IconComponent className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-white">{step.name}</div>
                      <div className="text-xs text-slate-400">{step.app}</div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-slate-300 mb-3">{step.description}</p>
                  
                  {step.url ? (
                    <a href={step.url} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                        Try Free <ExternalLink className="h-3 w-3 ml-1" />
                      </Button>
                    </a>
                  ) : (
                    <Button 
                      size="sm" 
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                      onClick={() => document.getElementById('processor')?.scrollIntoView({ behavior: 'smooth' })}
                    >
                      Start Now <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  )}
                </div>

                {/* Arrow between steps */}
                {index < steps.length - 1 && (
                  <ArrowRight className="h-6 w-6 text-slate-500 mx-2 hidden md:block" />
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom text */}
        <p className="text-center text-slate-400 text-sm mt-8">
          Join 50,000+ small business owners saving on legal costs
        </p>
      </div>
    </section>
  );
};

export default AffiliateSection;
